import * as Sentry from "@sentry/react";
import { isNil } from "lodash/fp";

import log from "@readai/common/logger";

import MemoryStorage from "./memoryStore";

const LOCAL_MEMORY_STORAGE = new MemoryStorage();
const SESSION_MEMORY_STORAGE = new MemoryStorage();

type StorageScope = "session" | "local";

const getMemoryStore = (scope: StorageScope): MemoryStorage => {
  return scope === "session" ? SESSION_MEMORY_STORAGE : LOCAL_MEMORY_STORAGE;
};

let hasStorageAccess: boolean | null = null;

export const getStorage = (scope: StorageScope) => {
  if (hasStorageAccess !== null && !hasStorageAccess) {
    return getMemoryStore(scope);
  }

  try {
    // only place window.localStorage and window.sessionStorage are used
    /* eslint-disable no-restricted-properties */
    const storage =
      scope === "session" ? window.sessionStorage : window.localStorage;
    /* eslint-enable no-restricted-properties */

    // handle when storage is not available
    if (!storage) {
      hasStorageAccess = false;
      return getMemoryStore(scope);
    }

    if (isNil(hasStorageAccess)) {
      storage.setItem("_readPermissionTest", "true");
      storage.removeItem("_readPermissionTest");
      hasStorageAccess = true;
    }

    return storage;
  } catch (error) {
    hasStorageAccess = false;
    if (error instanceof DOMException && error.name === "SecurityError") {
      log.warn(
        `no permission to access ${scope}, falling back to memory store`,
      );
    } else {
      log.warn(
        `error accessing ${scope} storage, falling back to memory store`,
      );
      Sentry.captureException(error);
    }
    return getMemoryStore(scope);
  }
};

export const getSessionStorage = () => getStorage("session");
export const getLocalStorage = () => getStorage("local");
